/**
 * Debug event handlers — captures toolUse, toolEnd, toolResult, textOutput,
 * moderationBlocked events for the developer debug panel.
 *
 * These handlers are additive — they don't consume events, just observe them.
 */

import type { Socket } from "socket.io-client";
import type {
  ServerToClientEvents,
  ClientToServerEvents,
} from "@amzn/a4k-creator-studio-shared";
import { useDebugStore } from "../debugStore";

type TypedSocket = Socket<ServerToClientEvents, ClientToServerEvents>;

export function registerDebugHandlers(socket: TypedSocket): void {
  const addEvent = useDebugStore.getState().addEvent;

  socket.on("toolUse", (data) => {
    if (!useDebugStore.getState().enabled) return;
    try {
      const content = typeof data.content === "string" ? JSON.parse(data.content) : data.content;
      addEvent({
        type: "toolUse",
        toolName: data.toolName,
        action: content?.action,
        contentTier: content?.contentTier,
        topics: content?.topics,
        raw: data,
      });
    } catch {
      addEvent({
        type: "toolUse",
        toolName: data.toolName,
        raw: data,
      });
    }
  });

  socket.on("toolResult", (data: any) => {
    if (!useDebugStore.getState().enabled) return;
    let result: any = null;
    try {
      result = typeof data?.content === "string" ? JSON.parse(data.content) : data?.result || data?.content;
    } catch { result = data; }
    if (typeof result === "string") {
      try { result = JSON.parse(result); } catch {}
    }
    addEvent({
      type: "toolResult",
      error: result?.error,
      message: result?.message || (result?.success ? "success" : undefined),
      raw: data,
    });
  });

  // Direct debug event from server (not through Sonic)
  (socket as any).on("debugSlowMode", (data: any) => {
    if (!useDebugStore.getState().enabled) return;
    addEvent({
      type: "slowModeVerdict",
      isAppropriate: data.verdict !== "blocked",
      reason: data.reason,
      latencyMs: data.latencyMs,
      verdict: data.verdict,
      raw: data,
    });
  });

  (socket as any).on("debugIpSanitizer", (data: any) => {
    if (!useDebugStore.getState().enabled) return;
    addEvent({
      type: "ipSanitizer",
      verdict: data.tier,
      reason: data.reason,
      latencyMs: data.latencyMs,
      raw: data,
    });
  });

  socket.on("moderationBlocked", (data) => {
    if (!useDebugStore.getState().enabled) return;
    addEvent({
      type: "moderationBlocked",
      elementType: data.type,
      elementId: data.id,
      stage: data.stage,
      reason: data.reason,
      raw: data,
    });
  });

  socket.on("textOutput", (data) => {
    if (!useDebugStore.getState().enabled) return;
    if (!data.content) return;
    const role = data.role;
    if (role === "USER") {
      addEvent({
        type: "kidTranscript",
        text: data.content,
      });
    } else if (role === "ASSISTANT") {
      // Filter protocol signals
      const trimmed = data.content.trim();
      if (trimmed.startsWith("{") && trimmed.endsWith("}")) {
        try {
          const parsed = JSON.parse(trimmed);
          if (parsed.interrupted !== undefined) return;
        } catch { /* not JSON */ }
      }
      addEvent({
        type: "agentResponse",
        text: data.content,
      });
    }
  });
}
